import { useEffect, useState } from 'react'
import apiClient from '../api/client'

interface UserRow {
  id: string
  email: string
  role: 'user' | 'admin'
  is_active: boolean
  created_at: string
}

function AdminUsersPage(): JSX.Element {
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resetId, setResetId] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')

  const fetchUsers = async () => {
    try {
      const res = await apiClient.get<UserRow[]>('/admin/users')
      setUsers(res.data as unknown as UserRow[])
      setError(null)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Failed to load users (admin only)')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleToggleActive = async (u: UserRow) => {
    try {
      await apiClient.patch(`/admin/users/${u.id}`, { is_active: !u.is_active })
      await fetchUsers()
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      alert(maybeAxios?.response?.data?.detail ?? 'Update failed')
    }
  }

  const handleRoleChange = async (u: UserRow, newRole: 'user' | 'admin') => {
    if (newRole === u.role) return
    try {
      await apiClient.patch(`/admin/users/${u.id}`, { role: newRole })
      await fetchUsers()
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      alert(maybeAxios?.response?.data?.detail ?? 'Role change failed')
    }
  }

  const handleReset = async (u: UserRow) => {
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters')
      return
    }
    try {
      await apiClient.post(`/admin/users/${u.id}/reset-password`, { new_password: newPassword })
      alert(`Password reset for ${u.email}`)
      setResetId(null)
      setNewPassword('')
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      alert(maybeAxios?.response?.data?.detail ?? 'Reset failed')
    }
  }

  if (loading) return <div className="page-header"><h1 className="page-title">Admin — Users</h1></div>
  if (error) return <div><p style={{ color: '#c00' }}>{error}</p></div>

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Admin — Users</h1>
      </div>
      <div className="detail-card detail-card-wide">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Active</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.email}</td>
                <td>
                  <select value={u.role} onChange={(e) => handleRoleChange(u, e.target.value as 'user' | 'admin')} className="form-input" style={{ padding: '4px 8px' }}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td>
                  <span className={`badge ${u.is_active ? 'badge--completed' : 'badge--failed'}`}>{u.is_active ? 'enabled' : 'disabled'}</span>
                </td>
                <td style={{ fontSize: 12, color: '#64748b' }}>{new Date(u.created_at).toLocaleString()}</td>
                <td style={{ display: 'flex', gap: 8 }}>
                  <button className="page-action-secondary" onClick={() => handleToggleActive(u)} style={{ padding: '4px 8px', fontSize: 12 }}>
                    {u.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="page-action-secondary" onClick={() => setResetId(resetId === u.id ? null : u.id)} style={{ padding: '4px 8px', fontSize: 12 }}>
                    Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p style={{ fontSize: 14, color: '#666', marginTop: 12 }}>No users found.</p>}
      </div>

      {resetId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 12 }}>Reset password</h3>
            <input type="password" placeholder="New password (≥8 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="form-input" style={{ width: '100%', marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="page-action-secondary" onClick={() => { setResetId(null); setNewPassword('') }}>Cancel</button>
              <button className="button-primary" onClick={() => handleReset(users.find((x) => x.id === resetId)!)}>Confirm reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsersPage
